import { useState, useCallback, useMemo } from 'react';
import { 
  listAttachments, 
  getStorageUsage,
  ATTACHMENT_LIMITS,
} from '../../../../../services/attachmentService';
import { isDocUploadAttachment } from '../../../../DocEditor/utils/fileOverrides';

/**
 * useAttachmentsData Hook
 * 
 * Manages attachments data state including:
 * - Loading attachments list
 * - Storage usage tracking
 * - Filter and sort state
 * - Version expansion state
 * - Computed filtered/sorted list
 */
const useAttachmentsData = (taskId) => {
  // Data state
  const [attachments, setAttachments] = useState([]);
  const [storageUsed, setStorageUsed] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter & sort state
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [expandedVersions, setExpandedVersions] = useState(new Set());

  // Load attachments from API
  const loadAttachments = useCallback(async () => {
    if (!taskId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const [attachmentsResult, storageResult] = await Promise.all([
        listAttachments(taskId),
        getStorageUsage(),
      ]);
      
      setAttachments(
        (attachmentsResult.attachments || []).filter((a) => !isDocUploadAttachment(a))
      );
      setStorageUsed(storageResult.storageUsed || 0);
    } catch (err) {
      console.error('Failed to load attachments:', err);
      setError('Failed to load attachments');
    } finally {
      setIsLoading(false);
    }
  }, [taskId]);

  // Toggle version history expansion
  const toggleVersions = useCallback((fileId) => {
    setExpandedVersions(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  // Add attachment to local state (after upload)
  // Groups with existing file if same filename exists
  const addAttachment = useCallback((uploadedFile) => {
    const newVersion = {
      id: uploadedFile.attachmentId,
      uploadedAt: uploadedFile.uploadedAt,
      size: uploadedFile.size,
      note: uploadedFile.note,
    };

    setAttachments(prev => {
      // Check if a file with the same filename already exists
      const existingIndex = prev.findIndex(a => a.filename === uploadedFile.filename);
      
      if (existingIndex >= 0) {
        // Add as a new version to existing file group
        const existing = prev[existingIndex];
        const updatedFile = {
          ...existing,
          // Update to new version's data (it's now the "current")
          id: uploadedFile.attachmentId,
          size: uploadedFile.size,
          uploadedAt: uploadedFile.uploadedAt,
          note: uploadedFile.note,
          // Prepend new version to versions array
          versions: [newVersion, ...existing.versions],
        };
        
        // Move to top of list and update
        const newList = [...prev];
        newList.splice(existingIndex, 1);
        return [updatedFile, ...newList];
      }
      
      // No existing file with same name - add as new entry
      return [{
        id: uploadedFile.attachmentId,
        filename: uploadedFile.filename,
        type: uploadedFile.fileType,
        mimeType: uploadedFile.mimeType,
        size: uploadedFile.size,
        uploadedAt: uploadedFile.uploadedAt,
        note: uploadedFile.note,
        versions: [newVersion],
      }, ...prev];
    });
    
    setStorageUsed(prev => prev + uploadedFile.size);
  }, []);

  // Remove attachment from local state
  const removeAttachment = useCallback((fileId) => {
    setAttachments(prev => prev.filter(a => a.id !== fileId));
  }, []);

  // Remove version from local state
  const removeVersion = useCallback((fileId, versionId, fileSize) => {
    setAttachments(prev => prev.map(a => {
      if (a.id === fileId) {
        const newVersions = a.versions.filter(v => v.id !== versionId);
        // If deleting the current version, promote the next one
        if (versionId === a.id && newVersions.length > 0) {
          const latest = newVersions[0];
          return {
            ...a,
            id: latest.id,
            uploadedAt: latest.uploadedAt,
            size: latest.size,
            note: latest.note,
            versions: newVersions,
          };
        }
        return { ...a, versions: newVersions };
      }
      return a;
    }));
    
    setStorageUsed(prev => Math.max(0, prev - fileSize));
  }, []);

  // Update storage used
  const updateStorageUsed = useCallback((delta) => {
    setStorageUsed(prev => Math.max(0, prev + delta));
  }, []);

  // Set error with auto-clear
  const setErrorWithAutoClear = useCallback((message) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Filter and sort attachments
  const filteredAttachments = useMemo(() => {
    return attachments
      .filter(a => filterType === 'all' || a.type === filterType)
      .sort((a, b) => {
        if (sortBy === 'date') {
          return new Date(b.uploadedAt) - new Date(a.uploadedAt);
        }
        if (sortBy === 'size') {
          return b.size - a.size;
        }
        // Sort by type (alphabetically)
        return (a.type || '').localeCompare(b.type || '');
      });
  }, [attachments, filterType, sortBy]);

  // Computed values
  const storageLimit = ATTACHMENT_LIMITS.maxStorage;
  const maxFileSize = ATTACHMENT_LIMITS.maxFileSize;
  const storagePercent = (storageUsed / storageLimit) * 100;
  const uniqueTypes = useMemo(() => [...new Set(attachments.map(a => a.type))], [attachments]);

  return {
    // Data
    attachments,
    filteredAttachments,
    storageUsed,
    storageLimit,
    maxFileSize,
    storagePercent,
    uniqueTypes,
    isLoading,
    error,
    
    // Filter/sort state
    filterType,
    setFilterType,
    sortBy,
    setSortBy,
    expandedVersions,
    toggleVersions,
    
    // Actions
    loadAttachments,
    addAttachment,
    removeAttachment,
    removeVersion,
    updateStorageUsed,
    setError: setErrorWithAutoClear,
    clearError,
  };
};

export default useAttachmentsData;
